import {renderMarkRule, renderNodeRule} from 'datocms-structured-text-to-plain-text'
import {
  isBlockquote,
  isCode,
  isHeading,
  isLink,
  isList,
  isListItem,
  isParagraph,
} from 'datocms-structured-text-utils'
import {Fragment} from 'react'
import {StructuredText} from 'react-datocms/structured-text'
import {DatoConstant} from '~/cms/components/Constants/DatoConstant'
import DisclaimerLink from '~/cms/components/SingleLineStructuredText/DisclaimerLink'
import {
  handleBoldMark,
  handleCodeMark,
  handleIOMark,
  handleSuperscriptMark,
} from '~/cms/components/WYSIWYG/_utils/markRules'
import {
  formatHeadingLink,
  handleHeadingNode,
  handleLinkNode,
  handleListItemNode,
  handleListNode,
  handleParagraphNode,
} from '~/cms/components/WYSIWYG/_utils/nodeRules'
import {ARTICLE_WYSIWYG_TYPE} from '~/design-system/hg/components/HgWYSIWYG'
import {handleDropCapMark} from '../../_utils/markRules'
import {handleBlockQuoteNode, handleCodeBlockNode} from '../../_utils/nodeRules'
import {type ArticleAsideAccompanyingBlockFragment} from './_generated/fragment.graphql'

type ArticleAsideAccompanyingBlockProps = {
  data?: ArticleAsideAccompanyingBlockFragment | null
}

export function ArticleAsideAccompanyingBlock({
  data,
}: ArticleAsideAccompanyingBlockProps) {
  if (!data) return null
  // only allow single node for accompanying block
  const dataWithFirstChildOnlyText = {
    ...data.text,
    value: {
      ...data.text.value,
      document: {
        ...data.text.value.document,
        children: [data.text.value.document.children[0]],
      },
    },
  }

  return (
    <Fragment key={data.id}>
      <StructuredText
        customMarkRules={[
          renderMarkRule('code', handleCodeMark),
          renderMarkRule('drop-cap', handleDropCapMark),
          renderMarkRule('io-font', handleIOMark),
          renderMarkRule('strong', handleBoldMark),
          renderMarkRule('superscript', handleSuperscriptMark),
        ]}
        customNodeRules={[
          renderNodeRule(isBlockquote, handleBlockQuoteNode),
          renderNodeRule(isCode, handleCodeBlockNode),
          renderNodeRule(isHeading, ({...props}) => {
            const {id} = formatHeadingLink(props.node)
            return handleHeadingNode({...props, id})
          }),
          renderNodeRule(isLink, handleLinkNode),
          renderNodeRule(isList, nodeProps =>
            handleListNode({...nodeProps, wysiwygType: ARTICLE_WYSIWYG_TYPE})
          ),
          renderNodeRule(isListItem, handleListItemNode),
          renderNodeRule(isParagraph, nodeProps =>
            handleParagraphNode({...nodeProps, wysiwygType: ARTICLE_WYSIWYG_TYPE})
          ),
        ]}
        renderLinkToRecord={({record}) => {
          switch (record.__typename) {
            case 'ConstantRecord':
              return <DatoConstant data={record} />
            case 'DisclaimerRecord':
              return <DisclaimerLink {...record} text={record.text} />
            default:
              return null
          }
        }}
        data={dataWithFirstChildOnlyText}
      />
    </Fragment>
  )
}
